"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useConsent } from "@/components/consent/ConsentProvider";
import { Button } from "@/components/ui/button";
import { extractBasePath } from "@/lib/utils/path";

/**
 * Non-blocking consent banner for UK/EEA/CH visitors (and unknown locations).
 * Accept and Reject share one button style — regulators expect equal weight.
 * Sits below Sheet/Dialog overlays (z-40 vs z-50).
 */
export function ConsentBanner() {
  const t = useTranslations("consent");
  const basePath = extractBasePath(usePathname());
  const { showBanner, acceptAll, rejectAll, openPreferences } = useConsent();

  if (!showBanner) return null;

  return (
    <section
      aria-label={t("bannerLabel")}
      className="fixed inset-x-0 bottom-0 z-40 p-2 sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-md sm:p-0"
    >
      <div className="rounded-xl bg-background p-3 text-sm shadow-lg ring-1 ring-foreground/10 sm:p-4">
        <p className="text-foreground">
          {/* Short on phones, full sentence from `sm` up. */}
          <span className="sm:hidden">{t("bannerTextShort")}</span>
          <span className="hidden sm:inline">{t("bannerText")}</span>{" "}
          <Link
            href={`${basePath}/policies/privacy-policy`}
            className="underline underline-offset-4"
          >
            {t("privacyPolicy")}
          </Link>
        </p>
        {/* Mobile: [Reject] [Accept] Customise in one row; sm+: Customise wraps below. */}
        <div className="mt-2 grid grid-cols-[1fr_1fr_auto] items-center gap-2 sm:mt-3 sm:grid-cols-2">
          <Button onClick={rejectAll}>{t("rejectAll")}</Button>
          <Button onClick={acceptAll}>{t("acceptAll")}</Button>
          <Button
            variant="link"
            size="sm"
            className="min-h-11 px-1 sm:col-span-2 sm:min-h-6 sm:justify-self-start sm:px-0"
            onClick={openPreferences}
          >
            {t("customise")}
          </Button>
        </div>
      </div>
    </section>
  );
}
